import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, Update, Save, Email, DeleteOutline } from '@mui/icons-material';

export type TemplateSaveOption = 'save_permanently' | 'discard' | 'this_email_only';

interface TemplateUpdateConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (option: TemplateSaveOption) => void;
  onCancel: () => void;
  templateName?: string;
}

export const TemplateUpdateConfirmationDialog: React.FC<TemplateUpdateConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  templateName = 'Email Template',
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Modern Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Update sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
              Template Modified
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              {templateName}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Content Area */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.primary', 
            lineHeight: 1.6, 
            fontSize: '0.938rem',
            fontWeight: 400
          }}
        >
          You have modified the <strong>{templateName}</strong> template. Select how you would like to proceed with these changes.
        </Typography>
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          flexDirection: 'column',
          alignItems: 'stretch',
          backgroundColor: '#fafafa',
        }}
      >
        {/* Primary Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {/* Save Permanently */}
          <Box
            onClick={() => onConfirm('save_permanently')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              p: 1.75,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(46, 125, 50, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(46, 125, 50, 0.4)',
                background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Save sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                Save Permanently
              </Typography>
              <Typography sx={{ fontSize: '0.813rem', opacity: 0.95, fontWeight: 400, lineHeight: 1.4 }}>
                Updates template for all future emails
              </Typography>
            </Box>
          </Box>

          {/* This Email Only */}
          <Box
            onClick={() => onConfirm('this_email_only')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              p: 1.75,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(25, 118, 210, 0.4)',
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Email sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                This Email Only
              </Typography>
              <Typography sx={{ fontSize: '0.813rem', opacity: 0.95, fontWeight: 400, lineHeight: 1.4 }}>
                Use modified content for this email only
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Discard Changes - Improved Visibility */}
        <Box
          onClick={() => onConfirm('discard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            p: 1.75,
            borderRadius: 2,
            backgroundColor: 'white',
            border: '2px solid #e57373',
            color: '#c62828',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(229, 115, 115, 0.15)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: '#ffebee',
              borderColor: '#ef5350',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(229, 115, 115, 0.25)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              backgroundColor: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: '1px solid #e57373',
            }}
          >
            <DeleteOutline sx={{ fontSize: 22, color: '#c62828' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5, color: '#c62828' }}>
              Discard Changes
            </Typography>
            <Typography sx={{ fontSize: '0.813rem', fontWeight: 400, lineHeight: 1.4, color: '#d32f2f' }}>
              Revert to original template content
            </Typography>
          </Box>
        </Box>

        {/* Cancel Button */}
        <Box sx={{ pt: 1 }}>
          <Button
            onClick={onClose}
            variant="text"
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.938rem',
              color: 'text.secondary',
              py: 1,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateUpdateConfirmationDialog;
